import {
  IPelicanCopyTradeAuthParams,
  IPelicanCopyTradeAuthResponse,
  IPelicanCopyTradeBroker,
  IPelicanCopyTradeCloseCopyTradeOrderParams,
  IPelicanCopyTradeCopierInfo,
  IPelicanCopyTradeCopierInfoParams,
  IPelicanCopyTradeCopySettings,
  IPelicanCopyTradeCopySettingsParams,
  IPelicanCopyTradeCreateCopySettingsParams,
  IPelicanCopyTradeEditCopierDrawdownParams,
  IPelicanCopyTradeEditCopyTradeOrderParams,
  IPelicanCopyTradeLinkCopierParams,
  IPelicanCopyTradeMissedTrades,
  IPelicanCopyTradeOrderOpenPositionParams,
  IPelicanCopyTradeProfile,
  IPelicanCopyTradeProfileParams,
  IPelicanCopyTradeRemoveCopierParams,
  IPelicanCopyTradeResetCopierParams,
  IPelicanCopyTradeServer,
  IPelicanCopyTradeSignalClosedColumns,
  IPelicanCopyTradeSignalOpenColumns,
  IPelicanCopyTradeStopCopySettingsParams,
  IPelicanCopyTradeStrategyDetailParams,
  IPelicanCopyTradeStrategyListParams,
  IPelicanCopyTradeStrategyListResponse,
  IPelicanCopyTradeUserInfo,
  IPelicanEditSectionSettings,
  IPelicanSectionSettings
} from '@aqx-core/common/api/copy-trade/model';
import { ECopyTradeFilter } from '@aqx-core/common/constants/copy-trade';
import { request } from '@aqx-core/common/request';

export default {
  auth: (params: IPelicanCopyTradeAuthParams) =>
    request.post<IPelicanCopyTradeAuthResponse>('/api/trade/copy/auth', params),
  getUserInfo: (signal?: AbortSignal) => request.get<IPelicanCopyTradeUserInfo>('/api/trade/copy/userinfo', { signal }),
  getAllStrategySections: () => request.get<IPelicanSectionSettings[]>('/api/trade/copy/strategy/sections/all'),
  getAvailableStrategySections: () => request.get<ECopyTradeFilter[]>('/api/trade/copy/strategy/sections/preference'),
  editAvailableStrategySections: (params: IPelicanEditSectionSettings) =>
    request.put('/api/trade/copy/strategy/sections/preference', params),
  getStrategyList: (params: IPelicanCopyTradeStrategyListParams, signal?: AbortSignal) => {
    const { section, ...queryParams } = params;
    const path = section ? `/api/trade/copy/strategy/discover/${section}` : '/api/trade/copy/strategy/discover';
    return request.get<IPelicanCopyTradeStrategyListResponse>(path, { params: queryParams, signal });
  },
  getStrategyDetail: (params: IPelicanCopyTradeStrategyDetailParams, signal?: AbortSignal) =>
    request.get(`/api/trade/copy/strategy/${params.strategyId}`, {
      params: { copierId: params.copierId, timeframe: params.timeframe },
      signal
    }),
  getStrategyImage: (params: IPelicanCopyTradeStrategyDetailParams) =>
    request.get<Blob | undefined>(`/api/trade/copy/strategy/${params.strategyId}/image`, { responseType: 'blob' }),
  getCopyTradeOpenPositions: (params: IPelicanCopyTradeStrategyDetailParams) =>
    request.get<IPelicanCopyTradeSignalOpenColumns[]>(`/api/trade/copy/strategy/${params.strategyId}/signals/open`),
  getCopyTradeHistory: (params: IPelicanCopyTradeStrategyDetailParams) =>
    request.get<IPelicanCopyTradeSignalClosedColumns[]>(
      `/api/trade/copy/strategy/${params.strategyId}/signals/closed`,
      { params: { startDate: params.startDate, endDate: params.endDate } }
    ),
  // Master data controller
  getBrokers: () => request.get<IPelicanCopyTradeBroker[]>('/api/trade/copy/brokers'),
  getServers: () => request.get<IPelicanCopyTradeServer[]>(`/api/trade/copy/servers`),
  // Copier
  linkCopier: (params: IPelicanCopyTradeLinkCopierParams) =>
    request.post<IPelicanCopyTradeCopierInfo>(`/api/trade/copy/copiers`, params),
  getCopierInfo: (params: IPelicanCopyTradeCopierInfoParams) =>
    request.get<IPelicanCopyTradeCopierInfo>(`/api/trade/copy/copiers/${params.copierId}`),
  removeCopier: (params: IPelicanCopyTradeRemoveCopierParams) =>
    request.delete(`/api/trade/copy/copiers/${params.copierId}`),
  switchCopierAccount: (copierId: string) => request.put<string>(`/api/trade/copy/copiers/switch`, copierId),
  resetCopierDrawdown: (params: IPelicanCopyTradeResetCopierParams) =>
    request.put(`/api/trade/copy/copiers/${params.copierId}/drawdown/reset`),
  editCopierDrawdown: (params: IPelicanCopyTradeEditCopierDrawdownParams) =>
    request.put<IPelicanCopyTradeCopierInfo>(`/api/trade/copy/copiers/${params.copierId}/drawdown`, params),
  // Copy settings
  getCopySettings: (params: IPelicanCopyTradeCopySettingsParams) =>
    request.get<IPelicanCopyTradeCopySettings>(
      `/api/trade/copy/copiers/${params.copierId}/strategies/${params.strategyId}/copy-settings`
    ),
  editCopySettings: (params: IPelicanCopyTradeCreateCopySettingsParams) =>
    request.put(`/api/trade/copy/copiers/${params.copierId}/strategies/${params.strategyId}/copy-settings`, params),
  createCopySettings: (params: IPelicanCopyTradeCreateCopySettingsParams) =>
    request.post(`/api/trade/copy/copiers/${params.copierId}/strategies/${params.strategyId}/copy-settings`, params),
  stopCopySettings: (params: IPelicanCopyTradeStopCopySettingsParams) =>
    request.delete(`/api/trade/copy/copiers/${params.copierId}/strategies/${params.strategyId}/copy-settings`, {
      data: {
        mode: params.mode
      }
    }),
  // assets
  getCopyTradeProfile: (params: IPelicanCopyTradeProfileParams) =>
    request.get<IPelicanCopyTradeProfile>(`/api/trade/copy/copiers/${params.copierId}/details`, {
      params: { timeframe: params.timeframe }
    }),
  getCopyTradeProfileImage: (params: IPelicanCopyTradeProfileParams) =>
    request.get<Blob | undefined>(`/api/trade/copy/copiers/${params.copierId}/image`, { responseType: 'blob' }),
  getCopierMissedTrades: (params: IPelicanCopyTradeProfileParams) =>
    request.get<IPelicanCopyTradeMissedTrades[]>(`/api/trade/copy/copiers/${params.copierId}/signals/missed`),
  getCopyTradeOrder: (params: IPelicanCopyTradeOrderOpenPositionParams) =>
    request.get<IPelicanCopyTradeSignalOpenColumns>(
      `/api/trade/copy/copiers/${params.copierId}/signals/open/${params.copiedSignalId}`
    ),
  editCopyTradeOrder: (params: IPelicanCopyTradeEditCopyTradeOrderParams) =>
    request.put<IPelicanCopyTradeSignalClosedColumns>(
      `/api/trade/copy/copiers/${params.copierId}/signals/open/${params.copiedSignalId}`,
      {
        stopPrice: params.stopPrice,
        limitPrice: params.limitPrice
      }
    ),
  closeCopyTradeOrder: (params: IPelicanCopyTradeCloseCopyTradeOrderParams) =>
    request.delete<IPelicanCopyTradeSignalClosedColumns>(
      `/api/trade/copy/copiers/${params.copierId}/signals/open/${params.copiedSignalId}`
    )
};
